import { type StateCreator } from 'zustand';
import { WatchlistCategory } from '#components/Watchlist/WatchlistCategory.ts';
import { type State, type Store, type WatchlistItem, type WatchlistStore } from './types.ts';

const compareWatchlistItems = (itemA: WatchlistItem, itemB: WatchlistItem) =>
  itemA.id === itemB.id && itemA.type === itemB.type;

export const createWatchlistStore: (initialState: Partial<State>) => StateCreator<Store, [], [], WatchlistStore> =
  initialState => (set, get) => ({
    addWatchlistItem: (item: WatchlistItem) => {
      const { watchlist } = get();

      if (watchlist.some(entry => compareWatchlistItems(entry, item))) {
        return;
      }

      set(state => ({
        watchlist: [item, ...state.watchlist],
      }));
    },
    getFirstWatchlistCategoryWithItem: (): WatchlistCategory | undefined => {
      const { watchlist } = get();

      if (watchlist.some(item => item.category === WatchlistCategory.Upcoming)) {
        return WatchlistCategory.Upcoming;
      }

      if (watchlist.some(item => item.category === WatchlistCategory.Completed)) {
        return WatchlistCategory.Completed;
      }

      if (watchlist.some(item => item.category === WatchlistCategory.InProduction)) {
        return WatchlistCategory.InProduction;
      }

      if (watchlist.some(item => item.category === WatchlistCategory.Planned)) {
        return WatchlistCategory.Planned;
      }

      if (watchlist.some(item => item.category === WatchlistCategory.Canceled)) {
        return WatchlistCategory.Canceled;
      }

      return;
    },
    hasWatchlist: () => {
      return get().watchlist.length > 0;
    },
    hasWatchlistItem: (item: WatchlistItem) => {
      return get().watchlist.some(entry => compareWatchlistItems(entry, item));
    },
    hasWatchlistItemCategory: (category: WatchlistCategory) => {
      const { watchlist } = get();
      return watchlist.some(item => item.category === category);
    },
    removeWatchlistItem: (item: WatchlistItem) => {
      const { watchlist } = get();

      if (!watchlist.some(entry => compareWatchlistItems(entry, item))) {
        return;
      }

      set(state => {
        const index = state.watchlist.findIndex(entry => compareWatchlistItems(entry, item));
        const newWatchlist = state.watchlist.toSpliced(index, 1);

        return {
          watchlist: newWatchlist,
        };
      });
    },
    watchlist: initialState.watchlist ?? [],
  });
